/**
 * Offline Storage Service using IndexedDB
 * Provides local storage for incidents and sync queue when offline
 */

const DB_NAME = 'ranger-mdt-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  INCIDENTS: 'incidents',
  SYNC_QUEUE: 'sync-queue',
  CACHED_DATA: 'cached-data'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create incidents store for offline-created incidents
      if (!database.objectStoreNames.contains(STORES.INCIDENTS)) {
        const incidentStore = database.createObjectStore(STORES.INCIDENTS, {
          keyPath: 'localId',
          autoIncrement: true
        });
        incidentStore.createIndex('status', 'syncStatus', { unique: false });
        incidentStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Create sync queue for pending API operations
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: 'id',
          autoIncrement: true
        });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Create cached data store for reference data
      if (!database.objectStoreNames.contains(STORES.CACHED_DATA)) {
        database.createObjectStore(STORES.CACHED_DATA, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Get a transaction and object store
 */
const getStore = async (storeName, mode = 'readonly') => {
  const database = await initDB();
  const transaction = database.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

// ============ INCIDENTS ============

/**
 * Save an incident locally (for offline creation)
 */
export const saveOfflineIncident = async (incidentData) => {
  const store = await getStore(STORES.INCIDENTS, 'readwrite');
  
  const incident = {
    ...incidentData,
    syncStatus: 'pending', // pending, syncing, synced, error
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString()
  };

  return new Promise((resolve, reject) => {
    const request = store.add(incident);
    request.onsuccess = () => {
      const savedIncident = { ...incident, localId: request.result };
      console.log('Incident saved locally:', savedIncident.localId);
      resolve(savedIncident);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all offline incidents
 */
export const getOfflineIncidents = async () => {
  const store = await getStore(STORES.INCIDENTS);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get pending (unsynced) incidents
 */
export const getPendingIncidents = async () => {
  const store = await getStore(STORES.INCIDENTS);
  const index = store.index('status');
  
  return new Promise((resolve, reject) => {
    const request = index.getAll('pending');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Update incident sync status
 */
export const updateIncidentSyncStatus = async (localId, status, serverId = null) => {
  const store = await getStore(STORES.INCIDENTS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const getRequest = store.get(localId);
    getRequest.onsuccess = () => {
      const incident = getRequest.result;
      if (!incident) {
        reject(new Error('Incident not found'));
        return;
      }
      
      incident.syncStatus = status;
      incident.lastModified = new Date().toISOString();
      if (serverId) {
        incident.serverId = serverId;
      }
      
      const putRequest = store.put(incident);
      putRequest.onsuccess = () => resolve(incident);
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Delete a synced incident from local storage
 */
export const deleteLocalIncident = async (localId) => {
  const store = await getStore(STORES.INCIDENTS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.delete(localId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all synced incidents
 */
export const clearSyncedIncidents = async () => {
  const incidents = await getOfflineIncidents();
  const syncedIncidents = incidents.filter(i => i.syncStatus === 'synced');
  
  for (const incident of syncedIncidents) {
    await deleteLocalIncident(incident.localId);
  }
  
  return syncedIncidents.length;
};

// ============ SYNC QUEUE ============

/**
 * Add an operation to the sync queue
 */
export const addToSyncQueue = async (operation) => {
  const store = await getStore(STORES.SYNC_QUEUE, 'readwrite');
  
  const queueItem = {
    ...operation,
    timestamp: new Date().toISOString(),
    status: 'pending', // pending, processing, completed, failed
    retryCount: 0
  };

  return new Promise((resolve, reject) => {
    const request = store.add(queueItem);
    request.onsuccess = () => {
      console.log('Added to sync queue:', request.result);
      resolve({ ...queueItem, id: request.result });
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all pending sync operations
 */
export const getPendingSyncOperations = async () => {
  const store = await getStore(STORES.SYNC_QUEUE);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const pending = request.result.filter(item => 
        item.status === 'pending' || item.status === 'failed'
      );
      resolve(pending);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Update sync queue item status
 */
export const updateSyncQueueItem = async (id, updates) => {
  const store = await getStore(STORES.SYNC_QUEUE, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (!item) {
        reject(new Error('Queue item not found'));
        return;
      }
      
      const updated = { ...item, ...updates };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Remove completed items from sync queue
 */
export const clearCompletedSyncItems = async () => {
  const store = await getStore(STORES.SYNC_QUEUE, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = async () => {
      const completed = request.result.filter(item => item.status === 'completed');
      for (const item of completed) {
        store.delete(item.id);
      }
      resolve(completed.length);
    };
    request.onerror = () => reject(request.error);
  });
};

// ============ CACHED DATA ============

/**
 * Cache reference data (like users, status types, etc.)
 */
export const cacheData = async (key, data) => {
  const store = await getStore(STORES.CACHED_DATA, 'readwrite');
  
  const cacheItem = {
    key,
    data,
    cachedAt: new Date().toISOString()
  };

  return new Promise((resolve, reject) => {
    const request = store.put(cacheItem);
    request.onsuccess = () => resolve(cacheItem);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get cached data
 */
export const getCachedData = async (key) => {
  const store = await getStore(STORES.CACHED_DATA);
  
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result?.data || null);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all cached data
 */
export const clearCache = async () => {
  const store = await getStore(STORES.CACHED_DATA, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// ============ UTILITY FUNCTIONS ============

/**
 * Get sync queue statistics
 */
export const getSyncStats = async () => {
  const incidents = await getOfflineIncidents();
  const syncQueue = await getPendingSyncOperations();
  
  return {
    pendingIncidents: incidents.filter(i => i.syncStatus === 'pending').length,
    syncingIncidents: incidents.filter(i => i.syncStatus === 'syncing').length,
    syncedIncidents: incidents.filter(i => i.syncStatus === 'synced').length,
    errorIncidents: incidents.filter(i => i.syncStatus === 'error').length,
    queuedOperations: syncQueue.length,
    totalLocalIncidents: incidents.length
  };
};

/**
 * Check if database is available
 */
export const isOfflineStorageAvailable = () => {
  return 'indexedDB' in window;
};

export default {
  initDB,
  saveOfflineIncident,
  getOfflineIncidents,
  getPendingIncidents,
  updateIncidentSyncStatus,
  deleteLocalIncident,
  clearSyncedIncidents,
  addToSyncQueue,
  getPendingSyncOperations,
  updateSyncQueueItem,
  clearCompletedSyncItems,
  cacheData,
  getCachedData,
  clearCache,
  getSyncStats,
  isOfflineStorageAvailable
};
